import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { useModalsDialogsContext } from './modals-dialogs-context';
/**
 * The hook to render a custom Modal.
 *
 * Allows passing a component in as `children`, which will be rendered when opening
 * the Modal.
 *
 * In most cases, you should go for the `useGenericDialog` version, as Modals are supposed
 * to be only text, images and a confirm and cancel button.
 *
 * @see {@link file://./use-generic-Modal.tsx}
 * @param options see {@link ModalOptions}
 * @returns an object containing functions to show and hide the Modal
 */
export function useModal(options) {
    const { showModalDialog, hideModalDialog } = useModalsDialogsContext();
    const id = React.useId();
    const handleClose = React.useCallback(() => {
        var _a;
        (_a = options.onClose) === null || _a === void 0 ? void 0 : _a.call(options);
        hideModalDialog(id);
    }, [hideModalDialog, id, options]);
    const show = React.useCallback((props) => {
        // onClose calls the context to remove the Modal from the stack
        var _a;
        const Lazy = React.lazy(options.lazyChildren);
        const element = (_jsx(React.Suspense, { fallback: (_a = options.skeleton) !== null && _a !== void 0 ? _a : null, children: _jsx(Lazy, { ...props, onClose: handleClose }) }));
        // Type guard to check if it's a split modal
        if (options.size === 'split') {
            const splitOptions = options;
            showModalDialog({
                id,
                type: 'modal',
                size: 'split',
                variant: splitOptions.variant,
                gridTemplateColumns: splitOptions.gridTemplateColumns,
                fullScreen: splitOptions.fullScreen,
                element,
                onClose: handleClose,
            });
        }
        else {
            const regularOptions = options;
            showModalDialog({
                id,
                type: 'modal',
                size: regularOptions.size,
                zIndex: regularOptions.zIndex,
                element,
                onClose: handleClose,
            });
        }
    }, [handleClose, id, options, showModalDialog]);
    return React.useMemo(() => ({
        showModal: show,
        hideModal: handleClose,
    }), [handleClose, show]);
}
